import React, { useContext, useEffect, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/CustomButton";
import addIcon from "../../assets/add_icon.png";
import searchIcon from "../../assets/search_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import Modal from "../../components/Modal";
import CustomInput from "../../components/CustomInput";
import axios from "axios";
import Loader from "../../components/Loader";
import RulerTable from "./RulerTable";
import ErrorBox from "../../components/ErrorBox";
import FabricDataEntryModal from "./FabricDataEntryModal";
import BackToDashboard from "../../components/BackToDashboard";
import { UserAuth } from "../../utils/AuthContext";
import AreYouSureModal from "../../components/AreYouSureModal";
import PageHeadline from "../../components/PageHeadline";
import useDebounce from "../../utils/useDebounce";

const UsingOrderList = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  // STATES
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingRulers, setLoadingRuler] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [showDataEntryModal, setShowDataEntryModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRulerId, setCurrentRulerId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [currentGroupCode, setCurrentGroupCode] = useState("");
  const [currentGroupDesign, setCurrentGroupDesign] = useState("");
  const [colorInputs, setColorInputs] = useState([
    { colorCode: "", colorName: "", availableStock: 0, purchasePrice: 0 },
  ]);

  // Error handling
  const [errorFields, setErrorFields] = useState({});
  const [formError, setFormError] = useState("");

  const [fabricData, setFabricData] = useState([]);

  /* VALIDATION FUNCTION */
  const validateFields = () => {
    let errors = {};
    let hasError = false;

    // Validate Ruler Code
    if (!currentGroupCode.trim()) {
      errors["code"] = t("rulercodeRequired");
      hasError = true;
    }

    // Validate Colors (at least one valid color is required)
    const validColors = colorInputs.filter(
      (c) => c.colorCode.trim() && c.colorName.trim()
    );

    if (validColors.length === 0) {
      errors["colors"] = t("atLeastOneColorRequired");
      hasError = true;
    } else {
      // Check each color for validity
      colorInputs.forEach((color, index) => {
        // Only validate if either field has content to avoid errors on empty rows
        if (color.colorCode.trim() || color.colorName.trim()) {
          if (!color.colorCode.trim()) {
            errors[`colorCode-${index}`] = t("colorCodeRequired");
            hasError = true;
          }
          if (!color.colorName.trim()) {
            errors[`colorName-${index}`] = t("colorNameRequired");
            hasError = true;
          }
        }
      });
    }

    setErrorFields(errors);
    return !hasError;
  };

  /* ENDPOINTS FUNCTIONS */

  // creating a ruler with it's colors
  const handleAddGroup = async () => {
    if (!validateFields()) {
      setFormError(t("pleaseEnterValidValues"));
      return;
    }

    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !currentGroupCode) return;

    const validColors = colorInputs.filter(
      (c) => c.colorCode.trim() && c.colorName.trim()
    );

    const colorsWithParsedValues = validColors.map((c) => {
      return {
        colorName: c.colorName,
        colorCode: c.colorCode,
        availableStock: c.availableStock,
        purchasePrice: parseFloat(c.purchasePrice),
      };
    });

    if (validColors.length === 0) return;

    const payload = {
      code: currentGroupCode,
      design: currentGroupDesign,
      organizationId: parseInt(organizationId),
      colors: colorsWithParsedValues,
    };

    try {
      setLoadingModal(true);
      setFormError("");

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createFabricRuler`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        // Add new data to the list
        setFabricData((prev) => [
          ...prev,
          {
            id: response.data.id || Math.random().toString(),
            code: currentGroupCode,
            colors: validColors,
          },
        ]);

        // Reset inputs
        resetForm();
        setShowDataEntryModal(false);
        getAllRulers();
      }
    } catch (err) {
      console.error("Network error:", err);
      setFormError(err.response?.data?.error || t("networkError"));
    } finally {
      setLoadingModal(false);
    }
  };

  // update a ruler with its colors
  const handleUpdateGroup = async () => {
    if (!validateFields()) {
      setFormError(t("emptyFields"));
      return;
    }

    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !currentGroupCode || !currentRulerId) return;

    const currentRuler = fabricData.find(
      (ruler) => ruler.id === currentRulerId
    );

    const validColorInputs = colorInputs.filter(
      (c) => c.colorCode.trim() && c.colorName.trim()
    );

    const updatedColors = validColorInputs.map((newColor) => {
      const existingColor = currentRuler?.colors?.find(
        (existingColor) => existingColor.colorCode === newColor.colorCode
      );

      const { to, ...colorWithoutTo } = existingColor || {};

      const reEvaluatedStock =
        existingColor?.averageCost !== newColor?.purchasePrice ||
        existingColor?.availableStock !== newColor?.availableStock;

      return {
        ...colorWithoutTo,
        colorCode: newColor.colorCode,
        colorName: newColor.colorName,
        availableStock: reEvaluatedStock ? newColor.availableStock : null,
        averageCost: reEvaluatedStock ? newColor.purchasePrice : null,
      };
    });

    const payload = {
      code: currentGroupCode,
      organizationId: parseInt(organizationId),
      colors: updatedColors,
    };

    try {
      setLoadingModal(true);
      setFormError("");

      const response = await axios.put(
        `${process.env.REACT_APP_URL}/updateFabricRuler/${currentRulerId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        // Update data in the list
        setFabricData((prev) =>
          prev.map((item) =>
            item.id === currentRulerId
              ? { ...item, code: currentGroupCode, colors: updatedColors }
              : item
          )
        );

        // Reset form and close modal
        resetForm();
        setShowDataEntryModal(false);
        getAllRulers();
      }
    } catch (err) {
      setFormError(err.response?.data?.error || t("networkError"));
    } finally {
      setLoadingModal(false);
    }
  };

  // get all rulers
  const getAllRulers = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingRuler(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllfabricRulers`,
        {
          params: {
            organizationId,
            code: searchQuery || undefined,
            page: pagination.currentPage,
            limit: 5,
          },
        }
      );

      if (response.status === 200 && response.data?.data) {
        const formatted = response.data.data.map((item) => ({
          id: item.id,
          code: item.code,
          colors: item.fabricColor.map((c) => ({
            id: c.id,
            colorCode: c.colorCode,
            colorName: c.colorName,
            to: `color-page/${c.id}`,
            availableStock: c.availableStock,
            measuringUnit: t("meter"),
            averageCost: c.averageCost,
            currency: "usd",
          })),
        }));
        setFabricData(formatted);
        setPagination({
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          totalRecords: response.data.pagination.total,
        });
      }
    } catch (err) {
      console.error("Failed to fetch rulers:", err);
    } finally {
      setLoadingRuler(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentRulerId) return;

    try {
      setLoadingModal(true);
      setFormError("");

      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/deleteFabricRuler/${currentRulerId}`
      );

      if (response.status === 200) {
        setFabricData((prev) =>
          prev.filter((ruler) => ruler.id !== currentRulerId)
        );

        resetForm();
        setShowDataEntryModal(false);
      }
    } catch (err) {
      console.error("Error deleting ruler:", err);
      setFormError(err.response?.data?.error || t("errorDeletingRuler"));
    } finally {
      setLoadingModal(false);
      setShowDeleteConfirmation(false);
    }
  };

  // HANDLER FUNCTIONS
  const handleShowModal = (isEdit = false) => {
    if (!isEdit) {
      resetForm();
    }
    setIsEditMode(isEdit);
    setShowDataEntryModal(!showDataEntryModal);
    setFormError("");
    setErrorFields({});
  };

  const handleEditRuler = (ruler) => {
    setCurrentRulerId(ruler.id);
    setCurrentGroupCode(ruler.code);
    setColorInputs(
      ruler.colors.map((color) => ({
        colorCode: color.colorCode,
        colorName: color.colorName,
        availableStock: color.availableStock,
        purchasePrice: color.averageCost,
      }))
    );
    handleShowModal(true);
  };

  const resetForm = () => {
    setCurrentGroupCode("");
    setCurrentGroupDesign("");
    setColorInputs([
      { colorCode: "", colorName: "", availableStock: 0, purchasePrice: 0 },
    ]);
    setCurrentRulerId(null);
    setIsEditMode(false);
    setErrorFields({});
    setFormError("");
  };

  const handleFormSubmit = () => {
    if (isEditMode) {
      handleUpdateGroup();
    } else {
      handleAddGroup();
    }
  };

  const handleDeleteRuler = async () => {
    setShowDeleteConfirmation(true);
  };

  /* SIDE EFFECTS */
  useEffect(() => {
    getAllRulers();
  }, [debouncedSearch, pagination.currentPage]);

  return (
    <PageWrapper>
      {/* Back button */}
      <div>
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={
            userData?.userType === "user"
              ? "/invoiceManagement"
              : "/fabricstore-dashboard"
          }
        />
      </div>
      <div className="App o-column o-column--gap-size_400">
        <PageHeadline
          title="manageRulersAndColors"
          classList="u-margin--top_700"
        />
        <div className="o-row o-row--horizontal-align_end">
          <CustomButton
            variant={"blank"}
            icon={addIcon}
            iconSize={16}
            title={t("addNewRuler")}
            onClick={() => handleShowModal(false)}
          />
        </div>

        <h2 className="u-margin_0 t-headline-2">{t("rulers")}</h2>
        <div className="o-row o-row--gap-size_400 o-row--horizontal-align_start">
          <CustomInput
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            resetSearch={() => {
              setSearchQuery("");
            }}
            thick
          />

          <CustomButton
            title={t("search")}
            icon={searchIcon}
            iconSize={15}
            classList="c-search-button"
          />
        </div>

        {loadingRulers ? (
          <Loader />
        ) : (
          <RulerTable data={fabricData} onEditRuler={handleEditRuler} />
        )}
      </div>

      {showDataEntryModal && (
        <Modal
          title={isEditMode ? t("editRuler") : t("addNewRuler")}
          onClose={() => handleShowModal(isEditMode)}
          footer={
            <>
              {loadingModal ? <Loader /> : null}
              {formError && <ErrorBox>{t(formError)}</ErrorBox>}
              <section className="edit-user-actions">
                <CustomButton
                  title={t("confirm")}
                  onClick={handleFormSubmit}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={loadingModal}
                />
                <CustomButton
                  title={t("cancel")}
                  onClick={() => handleShowModal(isEditMode)}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                />

                {isEditMode && (
                  <CustomButton
                    title={t("delete")}
                    onClick={handleDeleteRuler}
                    variant="red"
                    iconSize={16}
                    icon={deleteIcon}
                  />
                )}
              </section>
            </>
          }
        >
          <FabricDataEntryModal
            currentGroupCode={currentGroupCode}
            setCurrentGroupCode={setCurrentGroupCode}
            colorInputs={colorInputs}
            setCurrentGroupDesign={setCurrentGroupDesign}
            currentGroupDesign={currentGroupDesign}
            setColorInputs={setColorInputs}
            isEditMode={isEditMode}
            errorFields={errorFields}
          />
        </Modal>
      )}

      {pagination && (
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }))
              }
              disabled={pagination.currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
            />
            <span className="pagination-location">
              {t("page")}: {pagination.currentPage} - {pagination.totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }))
              }
              disabled={pagination.currentPage === pagination.totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
            />
          </div>
          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalRecords")}: {pagination.totalRecords}
            </span>
          </div>
        </section>
      )}

      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleConfirmDelete}
          customMessage={t("deleteRuler")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirmIconType="whiteCheck"
          loading={loadingModal}
          confirm
        />
      )}
    </PageWrapper>
  );
};

export default UsingOrderList;
